import axios from 'axios';
import { substituteObjectInArrayByValue } from '../../utilities/util';
import { FETCH_COMMISSION_COUNT, FETCH_COMMISSIONS } from './actionTypes';

export const GetCommissions =
	(searchObject, page = 1, pageSize = 10) =>
	(dispatch) => {
		const filter = {
			limit: pageSize,
			skip: (page - 1) * pageSize,
			order: 'creationDate DESC',
			include: [{ relation: 'salesPerson', scope: { fields: ['id', 'name'] } }],
		};
		return axios
			.get(`/Commissions?filter=${encodeURIComponent(JSON.stringify(filter))}`)
			.then((response) => dispatch({ type: FETCH_COMMISSIONS, commissions: response.data }));
	};

export const GetCommissionCount = () => (dispatch) => {
	return axios
		.get(`/Commissions/count`)
		.then((response) => dispatch({ type: FETCH_COMMISSION_COUNT, count: response.data.count }));
};

export const LoadSalesmen = () => {
	return axios.get(`/Commissions/getSalesmen`).then((response) => response.data);
};

export const LoadCommission = (commissionId) => {
	return axios.get(`/Commissions/${commissionId}/getInfo`).then((response) => response.data);
};

export const LoadCommissionForEdit = (commissionId) => {
	return axios.get(`/Commissions/${commissionId}/getInfoForEdit`).then((response) => response.data);
};

export const LoadInvoices = (salesPersonId) => {
	return axios.get(`/Commissions/getInvoices/${salesPersonId}`).then((response) => response.data);
};

export const LoadCommissionAnnualPercent = async () => {
	const filter = {
		where: { name: 'COMMISSION_PERCENT_ANNUAL' },
	};
	const response = await axios.get(`/ConfigurationTables?filter=${encodeURIComponent(JSON.stringify(filter))}`);
	return response.data[0];
};

export const LoadCommissionSalesPercent = async () => {
	const filter = {
		where: { name: 'COMMISSION_PERCENT_SALES' },
	};
	const response = await axios.get(`/ConfigurationTables?filter=${encodeURIComponent(JSON.stringify(filter))}`);
	return response.data[0];
};

export const LoadCommissionProductsConfig = async (name) => {
	const filter = {
		where: { name },
	};
	const response = await axios.get(`/ConfigurationTables?filter=${encodeURIComponent(JSON.stringify(filter))}`);
	const data = response.data[0];
	data.value = JSON.parse(data.value);
	return data;
};

export const SaveCommissionProductsConfig = async (id, selectProductIds, initialProductIds) => {
	await axios.post(`/Commissions/setCommissionableProductConfig`, {
		configurationId: id,
		productsIds: selectProductIds,
		initialProductIds,
	});
};

export const SaveCustomPriceCommissionProductsConfig = async (id, selectProductIds, initialProductIds) => {
	await axios.post(`/Commissions/setCustomPriceCommissionableProductConfig`, {
		configurationId: id,
		productsIds: selectProductIds,
		initialProductIds,
	});
};

export const SaveCommissionPercentConfig = async (id, value) => {
	await axios.patch(`/ConfigurationTables/${id}`, { value: value / 100 });
};

export const LoadCommissionBrackets = async (bracketType) => {
	const filter = { where: { type: bracketType } };
	const response = await axios.get(`/CommissionBrackets?filter=${encodeURIComponent(JSON.stringify(filter))}`);
	return response.data;
};

export const BeforeEditCommissionBrackets = async (commissionBrackets, index, updatedBracket) => {
	try {
		const tasks = [];
		let bracketBefore = commissionBrackets[index - 1];
		let bracketAfter = commissionBrackets[index + 1];

		if (bracketAfter) {
			const bracketA = {
				...bracketAfter,
				min: updatedBracket.max + 1,
			};
			tasks.push(EditCommissionBracket(bracketA));
		}
		if (bracketBefore) {
			const bracketB = {
				...bracketBefore,
				max: updatedBracket.min - 1,
			};
			tasks.push(EditCommissionBracket(bracketB));
		}
		await Promise.all([...tasks, EditCommissionBracket(updatedBracket)]);
	} catch (error) {
		return error;
	}
};

export const EditCommissionBracket = async (bracket) => {
	await axios.patch(`/CommissionBrackets/${bracket.id}`, bracket);
};

export const AddCommission = (commissionInfo) => {
	return axios.post('/Commissions/addCommission', { commissionInfo }).then((response) => response.data);
};

export const GetNoCommissionInvoicesCount = (salesPersonId) => {
	return axios.get(`/Commissions/getNoCommissionInvoicesCount/${salesPersonId}`).then((response) => response.data);
};

export const LoadNoCommissionInvoices = (salesPersonId) => {
	return axios({
		url: `/Commissions/getNoCommissionInvoices`,
		method: 'GET',
		responseType: 'json',
		params: { salesPersonId },
	}).then((response) => response.data);
};

export const UpdateCommission = (commissionInfo, commissionId) => {
	return axios({
		url: `/Commissions/updateCommission`,
		method: 'POST',
		responseType: 'json',
		params: { commissionId, commissionInfo },
	}).then((response) => response.data);
};

export const LoadCommissionProductInfo = async () => {
	const response = await axios.get(`/Commissions/getCommissionProductInfo`);
	return response.data;
};

export const LoadCommissionableProductIds = () => {
	return axios.get(`/Commissions/getCommissionableProductIds`).then((response) => response.data);
};

export const CheckCommissionInvoiceEntryExistance = (invoiceId) => {
	const filter = {
		fields: ['invoiceId', 'commissionId'],
		where: { invoiceId },
	};
	return axios
		.get(`/CommissionInvoiceEntries?filter=${encodeURIComponent(JSON.stringify(filter))}`)
		.then((response) => response.data);
};

export const LoadSalespersonPendingCommissions =
	(salesPersonId, page = 1, pageSize = 10) =>
	(dispatch) => {
		return axios({
			url: `/Commissions/getSalespersonPendingCommissions`,
			method: 'GET',
			responseType: 'json',
			params: { salesPersonId, page, pageSize },
		}).then((response) => dispatch({ type: FETCH_COMMISSIONS, commissions: response.data }));
	};

export const GetSalesPersonRecordCount = (salesPersonId) => (dispatch) => {
	return axios
		.get(`/Commissions/count`, { params: { where: { salesPersonId } } })
		.then((response) => dispatch({ type: FETCH_COMMISSION_COUNT, count: response.data.count }));
};

const updateSingleCommission = (updatedCommission, dispatch, getState) => {
	const state = getState();
	const commissions = state.commissions.commissions;
	dispatch({
		type: FETCH_COMMISSIONS,
		commissions: substituteObjectInArrayByValue('id', updatedCommission.id, commissions, updatedCommission),
	});
};

const removeSingleCommission = (commissionId, dispatch, getState) => {
	const state = getState();
	const commissions = state.commissions.commissions;
	const newCommissions = [];
	commissions.forEach((commission) => (commission.id !== commissionId ? newCommissions.push(commission) : null));
	dispatch({ type: FETCH_COMMISSIONS, commissions: newCommissions });
};

export const RejectCommission = (commissionId) => (dispatch, getState) => {
	return axios
		.post(`/Commissions/${commissionId}/reject`)
		.then(() => removeSingleCommission(commissionId, dispatch, getState));
};

export const ApproveCommission = (commissionId) => (dispatch, getState) => {
	return axios
		.post(`/Commissions/${commissionId}/approve`)
		.then(() => removeSingleCommission(commissionId, dispatch, getState));
};

export const PayCommission = (commissionId, data) => (dispatch, getState) => {
	return axios({
		url: `/Commissions/${commissionId}/pay`,
		method: 'POST',
		responseType: 'json',
		params: { commissionId, data },
	}).then((response) => updateSingleCommission(response.data, dispatch, getState));
};
